import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../App";
import { API } from "../App";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Separator } from "../components/ui/separator";
import {
  Tooltip as TooltipUI,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../components/ui/tooltip";
import {
  ShoppingBag, DollarSign, Package, Clock, CheckCircle, XCircle,
  Truck, Search, RefreshCw, Eye, AlertTriangle, ChevronLeft,
  ChevronRight, Shield, Lock, Download, Calendar, TrendingUp,
  BarChart3, Phone, Mail, MapPin, Crown, User, Save, Activity,
} from "lucide-react";
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer,
} from "recharts";
import { format, subDays, parseISO } from "date-fns";

// Helper function to format delivery date from yyyy-MM-dd to dd-MM-yyyy
const formatDeliveryDate = (dateStr) => {
  if (!dateStr) return "";
  try {
    // Handle yyyy-MM-dd format
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
      const [year, month, day] = dateStr.split("-");
      return `${day}-${month}-${year}`;
    }
    return dateStr;
  } catch {
    return dateStr;
  }
};

// Store tag component
const StoreTag = ({ tag }) => {
  const colors = {
    W: "bg-green-100 text-green-700 border-green-200",
    C: "bg-red-100 text-red-700 border-red-200",
    WH: "bg-blue-100 text-blue-700 border-blue-200",
  };
  const labels = { W: "Woolworths", C: "Coles", WH: "ChemistWH" };
  return (
    <span
      className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold border ${colors[tag] || "bg-gray-100 text-gray-600 border-gray-200"}`}
      title={labels[tag] || "Unknown"}
    >
      {tag}
    </span>
  );
};

// Traffic light indicator
const StatusLight = ({ status, name }) => {
  const colors = {
    green: "bg-emerald-500 shadow-emerald-300",
    yellow: "bg-yellow-500 shadow-yellow-300",
    red: "bg-red-500 shadow-red-300",
    unknown: "bg-gray-400 shadow-gray-300",
  };
  return (
    <div className="flex items-center gap-2" data-testid={`health-${name.toLowerCase().replace(/\s/g, "-")}`}>
      <div className={`w-2.5 h-2.5 rounded-full ${colors[status] || colors.unknown} shadow-sm`} />
      <span className="text-xs text-slate-600">{name}</span>
    </div>
  );
};

const FoundersDashboard = () => {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [isFounder, setIsFounder] = useState(null);
  const [isDriver, setIsDriver] = useState(false);
  const [userRole, setUserRole] = useState(null);
  const [accessError, setAccessError] = useState(null);
  const [orders, setOrders] = useState([]);
  const [allOrders, setAllOrders] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1, page_size: 100, total_count: 0, total_pages: 0, has_next: false, has_prev: false,
  });
  const [stats, setStats] = useState({
    total_orders: 0, pending_orders: 0, confirmed_orders: 0,
    delivered_orders: 0, total_revenue: 0, today_orders: 0,
  });
  const [filters, setFilters] = useState({
    status: "all", search: "", dateRange: "all", startDate: "", endDate: "",
  });
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showCharts, setShowCharts] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [systemHealth, setSystemHealth] = useState(null);
  const [healthLoading, setHealthLoading] = useState(false);
  const [reconciliation, setReconciliation] = useState({});
  const [savingReconciliation, setSavingReconciliation] = useState(false);
  const [driverAllowedStatuses, setDriverAllowedStatuses] = useState([]);

  // Check founder/driver access
  useEffect(() => {
    const checkAccess = async () => {
      if (!token) {
        setTimeout(() => {
          if (!token) { setIsFounder(false); setAccessError("Please login to access this page"); setLoading(false); }
        }, 1500);
        return;
      }
      try {
        const headers = { Authorization: `Bearer ${token}` };
        const response = await axios.get(`${API}/founders/check`, { headers });
        const { is_founder, is_driver, role } = response.data;
        setUserRole(role);
        
        if (is_founder) { 
          setIsFounder(true); 
          setIsDriver(false);
          fetchData(1); 
          fetchSystemHealth(); 
        } else if (is_driver) {
          setIsFounder(false);
          setIsDriver(true);
          fetchDriverOrders();
        } else { 
          setIsFounder(false); 
          setIsDriver(false);
          setAccessError("Access restricted to BundleCart founders and drivers"); 
          setLoading(false); 
        }
      } catch {
        setIsFounder(false); setAccessError("Unable to verify access"); setLoading(false);
      }
    };
    checkAccess();
  }, [token]);

  // Fetch driver-specific orders
  const fetchDriverOrders = async () => {
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API}/driver/orders`, { headers });
      setOrders(response.data.orders || []);
      setDriverAllowedStatuses(response.data.allowed_statuses || ["delivered"]);
    } catch (error) {
      if (error.response?.status === 403) { 
        setAccessError("Driver access required"); 
      } else {
        toast.error("Failed to load orders");
      }
    } finally { 
      setLoading(false); 
    }
  };

  const fetchData = async (page = pagination.page) => {
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const params = new URLSearchParams({ page: page.toString(), page_size: "100" });
      if (filters.status && filters.status !== "all") params.append("status", filters.status);
      if (filters.search) params.append("search", filters.search);
      if (filters.startDate) params.append("start_date", filters.startDate);
      if (filters.endDate) params.append("end_date", filters.endDate);
      const response = await axios.get(`${API}/founders/orders?${params}`, { headers });
      setOrders(response.data.orders || []);
      setAllOrders(response.data.all_orders_for_charts || response.data.orders || []);
      setPagination(response.data.pagination || pagination);
      setStats(response.data.stats || stats);
    } catch (error) {
      if (error.response?.status === 403) { setIsFounder(false); setAccessError("Founders access required"); }
      else toast.error("Failed to load orders");
    } finally { setLoading(false); }
  };

  const fetchSystemHealth = async () => {
    setHealthLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API}/system/health`, { headers });
      setSystemHealth(response.data);
    } catch { /* ignore */ }
    finally { setHealthLoading(false); }
  };

  useEffect(() => {
    if (isFounder && token) {
      const debounce = setTimeout(() => fetchData(1), 300);
      return () => clearTimeout(debounce);
    }
  }, [filters.status, filters.search, filters.startDate, filters.endDate]);

  const handleDateRangeChange = (range) => {
    const today = new Date();
    let startDate = "", endDate = format(today, "yyyy-MM-dd");
    switch (range) {
      case "today": startDate = format(today, "yyyy-MM-dd"); break;
      case "7days": startDate = format(subDays(today, 7), "yyyy-MM-dd"); break;
      case "30days": startDate = format(subDays(today, 30), "yyyy-MM-dd"); break;
      case "90days": startDate = format(subDays(today, 90), "yyyy-MM-dd"); break;
      default: startDate = ""; endDate = ""; break;
    }
    setFilters({ ...filters, dateRange: range, startDate, endDate });
  };

  const chartData = useMemo(() => {
    const last30Days = [];
    const today = new Date();
    for (let i = 29; i >= 0; i--) {
      const date = subDays(today, i);
      const dateStr = format(date, "yyyy-MM-dd");
      const dayOrders = allOrders.filter(o => o.created_at?.startsWith(dateStr));
      last30Days.push({
        date: format(date, "dd MMM"),
        orders: dayOrders.length,
        revenue: dayOrders.reduce((sum, o) => sum + (o.total || 0), 0),
      });
    }
    return last30Days;
  }, [allOrders]);

  const exportToCSV = async () => {
    setExporting(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const params = new URLSearchParams({ page: "1", page_size: "10000" });
      if (filters.status && filters.status !== "all") params.append("status", filters.status);
      if (filters.startDate) params.append("start_date", filters.startDate);
      if (filters.endDate) params.append("end_date", filters.endDate);
      const response = await axios.get(`${API}/founders/orders?${params}`, { headers });
      const exportOrders = response.data.orders || [];
      const csvHeaders = [
        "Order Number", "Date", "Customer Name", "Email", "Phone", "Suburb", "Postcode",
        "Member", "VIP", "Items", "Stores", "Subtotal", "Convenience Fee", "Delivery Fee",
        "Membership Fee", "Total", "Payment Status", "Transaction ID", "Order Status",
        "Delivery Date", "Delivery Time", "Instructions",
        "Coles Receipt", "Woolworths Receipt", "ChemistWH Receipt", "Admin Comments", "Delivery Driver",
      ];
      const csvRows = exportOrders.map(o => [
        o.order_number || o.id?.slice(0, 8),
        o.created_at ? format(parseISO(o.created_at), "dd-MM-yyyy HH:mm") : "",
        o.customer_name || "", o.email, o.customer_phone || "",
        o.delivery_details?.suburb || "", o.delivery_details?.postcode || "",
        o.is_member_user ? "Member" : "Guest", o.is_vip ? "VIP" : "",
        o.items?.length || 0,
        [...new Set((o.items || []).map(i => i.store_tag))].join("/"),
        o.subtotal?.toFixed(2), o.convenience_fee?.toFixed(2), o.delivery_fee?.toFixed(2),
        (o.membership_fee || 0).toFixed(2), o.total?.toFixed(2),
        o.payment_status, o.transaction_id || "", o.order_status,
        formatDeliveryDate(o.delivery_details?.delivery_date) || "", o.delivery_details?.delivery_time || "",
        o.delivery_details?.special_instructions || "",
        o.coles_receipt_id || "", o.woolworths_receipt_id || "",
        o.chemist_receipt_id || "", o.admin_comments || "", o.delivery_driver || "",
      ]);
      const csvContent = [csvHeaders.join(","), ...csvRows.map(row => row.map(c => `"${c}"`).join(","))].join("\n");
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      link.setAttribute("href", URL.createObjectURL(blob));
      link.setAttribute("download", `bundlecart_orders_${format(new Date(), "yyyyMMdd_HHmm")}.csv`);
      document.body.appendChild(link); link.click(); document.body.removeChild(link);
      toast.success(`Exported ${exportOrders.length} orders to CSV`);
    } catch { toast.error("Failed to export orders"); }
    finally { setExporting(false); }
  };

  const updateOrderStatus = async (orderId, newStatus) => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.put(`${API}/orders/${orderId}/status`, { status: newStatus }, { headers });
      toast.success(`Order status updated to ${newStatus.replace(/_/g, " ")}`);
      
      // Immediately update local state for instant UI feedback
      setOrders(prev => prev.map(o => 
        (o.order_number === orderId || o.id === orderId) 
          ? { ...o, order_status: newStatus } 
          : o
      ));
      
      // Also update selectedOrder if it's open
      if (selectedOrder && (selectedOrder.order_number === orderId || selectedOrder.id === orderId)) {
        setSelectedOrder(prev => ({ ...prev, order_status: newStatus }));
      }
      
      // Refresh data in background for drivers (to get updated list if status moves out of view)
      if (isDriver) {
        fetchDriverOrders();
      }
    } catch { toast.error("Failed to update order status"); }
  };

  const openOrderDetail = (order) => {
    setSelectedOrder(order);
    setReconciliation({
      coles_receipt_id: order.coles_receipt_id || "",
      woolworths_receipt_id: order.woolworths_receipt_id || "",
      chemist_receipt_id: order.chemist_receipt_id || "",
      admin_comments: order.admin_comments || "",
      delivery_driver: order.delivery_driver || "",
    });
  };

  const saveReconciliation = async () => {
    if (!selectedOrder) return;
    setSavingReconciliation(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.put(`${API}/orders/${selectedOrder.order_number || selectedOrder.id}/reconciliation`, reconciliation, { headers });
      toast.success("Reconciliation saved");
      // Update local state
      setOrders(prev => prev.map(o => o.id === selectedOrder.id ? { ...o, ...reconciliation } : o));
      setSelectedOrder(prev => ({ ...prev, ...reconciliation }));
    } catch { toast.error("Failed to save reconciliation"); }
    finally { setSavingReconciliation(false); }
  };

  // Quick update for delivery driver from table
  const updateReconciliation = async (orderId, data) => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.put(`${API}/orders/${orderId}/reconciliation`, data, { headers });
      toast.success("Driver assigned");
    } catch { toast.error("Failed to assign driver"); }
  };

  // Badge components
  const StatusBadge = ({ status }) => {
    const config = {
      pending: { color: "bg-yellow-100 text-yellow-800", icon: Clock },
      confirmed: { color: "bg-blue-100 text-blue-800", icon: CheckCircle },
      ready_for_delivery: { color: "bg-indigo-100 text-indigo-800", icon: Package },
      delivered: { color: "bg-green-100 text-green-800", icon: CheckCircle },
      cancelled: { color: "bg-red-100 text-red-800", icon: XCircle },
    };
    const c = config[status] || config.pending;
    const Icon = c.icon;
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold ${c.color}`}>
        <Icon className="w-2.5 h-2.5" />{status?.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase())}
      </span>
    );
  };

  const PaymentBadge = ({ status, transactionId }) => {
    const isPaid = status === "paid";
    return (
      <div>
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold ${isPaid ? "bg-green-100 text-green-800" : "bg-orange-100 text-orange-800"}`}>
          {isPaid ? <CheckCircle className="w-2.5 h-2.5" /> : <AlertTriangle className="w-2.5 h-2.5" />}
          {isPaid ? "Paid" : "Pending"}
        </span>
        {transactionId && <p className="text-[9px] text-slate-400 mt-0.5 font-mono">{transactionId}</p>}
      </div>
    );
  };

  const MemberBadge = ({ isMember }) => (
    <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold ${isMember ? "bg-amber-100 text-amber-800" : "bg-slate-100 text-slate-600"}`}>
      {isMember ? <Crown className="w-2.5 h-2.5" /> : <User className="w-2.5 h-2.5" />}
      {isMember ? "Member" : "Guest"}
    </span>
  );

  const VipBadge = ({ count }) => (
    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-gradient-to-r from-purple-500 to-pink-500 text-white text-[10px] font-bold" title={`${count} successful orders`}>
      <Crown className="w-2.5 h-2.5" /> VIP
    </span>
  );

  // Access denied / loading
  if (isFounder === false && isDriver === false && accessError) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Lock className="w-8 h-8 text-red-600" />
          </div>
          <h1 className="text-2xl font-bold text-slate-900 mb-2">Access Restricted</h1>
          <p className="text-slate-600 mb-6">{accessError || "This page is restricted to BundleCart founders and drivers."}</p>
          <Button onClick={() => navigate("/")} className="w-full">Return to Home</Button>
        </div>
      </div>
    );
  }

  if (loading && isFounder === null && !isDriver) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 animate-spin text-sky-600 mx-auto mb-4" />
          <p className="text-slate-600">Verifying access...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="founders-dashboard">
      <div className="container-main py-6">
        {/* System Health Status Bar - Founders Only */}
        {!isDriver && (
          <div className="bg-white rounded-xl p-3 shadow-sm border border-slate-200 mb-4" data-testid="system-health-bar">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-1.5">
                <Activity className="w-4 h-4 text-slate-500" />
                <span className="text-xs font-semibold text-slate-700 mr-3">Scraper Status</span>
                {systemHealth?.integrations ? (
                  Object.values(systemHealth.integrations).map((api) => (
                    <StatusLight key={api.name} status={api.status} name={api.name} />
                  ))
                ) : (
                  <span className="text-xs text-slate-400">{healthLoading ? "Checking..." : "Click refresh"}</span>
                )}
              </div>
              <Button variant="ghost" size="sm" onClick={fetchSystemHealth} disabled={healthLoading} className="h-7 text-xs">
                <RefreshCw className={`w-3 h-3 mr-1 ${healthLoading ? "animate-spin" : ""}`} />
                Check
              </Button>
            </div>
          </div>
        )}

        {/* Header */}
        <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
          <div>
            <div className="flex items-center gap-2 mb-1">
              {isDriver ? (
                <>
                  <Truck className="w-5 h-5 text-cyan-600" />
                  <span className="text-xs font-medium text-cyan-600 uppercase tracking-wider">Driver Portal</span>
                </>
              ) : (
                <>
                  <Shield className="w-5 h-5 text-sky-600" />
                  <span className="text-xs font-medium text-sky-600 uppercase tracking-wider">Founders Only</span>
                </>
              )}
            </div>
            <h1 className="text-2xl font-bold text-slate-900 font-['Outfit']">
              {isDriver ? "My Deliveries" : "BundleCart Dashboard"}
            </h1>
          </div>
          <div className="flex gap-2">
            {!isDriver && (
              <>
                <Button variant="outline" size="sm" onClick={() => navigate("/founders/manage-accounts")} data-testid="manage-accounts-btn">
                  <User className="w-4 h-4 mr-1" />Accounts
                </Button>
                <Button variant="outline" size="sm" onClick={() => setShowCharts(!showCharts)} data-testid="toggle-charts">
                  <BarChart3 className="w-4 h-4 mr-1" />{showCharts ? "Hide Charts" : "Charts"}
                </Button>
                <Button variant="outline" size="sm" onClick={exportToCSV} disabled={exporting} data-testid="export-btn">
                  <Download className={`w-4 h-4 mr-1 ${exporting ? "animate-pulse" : ""}`} />{exporting ? "..." : "Export CSV"}
                </Button>
              </>
            )}
            <Button onClick={() => isDriver ? fetchDriverOrders() : fetchData()} variant="outline" size="sm" disabled={loading} data-testid="refresh-btn">
              <RefreshCw className={`w-4 h-4 mr-1 ${loading ? "animate-spin" : ""}`} />Refresh
            </Button>
          </div>
        </div>

        {/* Stats Cards - Founders Only */}
        {!isDriver && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-5">
            {[
              { label: "Total Orders", value: stats.total_orders, color: "text-slate-900" },
              { label: "Pending", value: stats.pending_orders, color: "text-yellow-600" },
              { label: "Confirmed", value: stats.confirmed_orders, color: "text-blue-600" },
              { label: "Delivered", value: stats.delivered_orders, color: "text-green-600" },
              { label: "Today", value: stats.today_orders, color: "text-purple-600" },
              { label: "Revenue", value: `$${stats.total_revenue.toLocaleString()}`, color: "text-emerald-600" },
            ].map(({ label, value, color }) => (
              <div key={label} className="bg-white rounded-xl p-3 shadow-sm border border-slate-200">
                <p className="text-[10px] text-slate-500 mb-0.5">{label}</p>
                <p className={`text-xl font-bold ${color}`}>{value}</p>
              </div>
            ))}
          </div>
        )}

        {/* Charts - Founders Only */}
        {!isDriver && showCharts && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-5">
            <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-200">
              <div className="flex items-center gap-2 mb-3">
                <TrendingUp className="w-4 h-4 text-emerald-600" />
                <h3 className="font-semibold text-sm text-slate-900">Revenue (30 Days)</h3>
              </div>
              <ResponsiveContainer width="100%" height={200}>
                <LineChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="date" tick={{ fontSize: 9 }} stroke="#94a3b8" />
                  <YAxis tick={{ fontSize: 9 }} stroke="#94a3b8" tickFormatter={(v) => `$${v}`} />
                  <Tooltip formatter={(v) => [`$${v.toFixed(2)}`, "Revenue"]} contentStyle={{ borderRadius: 8, fontSize: 12 }} />
                  <Line type="monotone" dataKey="revenue" stroke="#10b981" strokeWidth={2} dot={{ fill: "#10b981", r: 2 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
            <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-200">
              <div className="flex items-center gap-2 mb-3">
                <BarChart3 className="w-4 h-4 text-sky-600" />
                <h3 className="font-semibold text-sm text-slate-900">Orders (30 Days)</h3>
              </div>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="date" tick={{ fontSize: 9 }} stroke="#94a3b8" />
                  <YAxis tick={{ fontSize: 9 }} stroke="#94a3b8" />
                  <Tooltip contentStyle={{ borderRadius: 8, fontSize: 12 }} />
                  <Bar dataKey="orders" fill="#0ea5e9" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="bg-white rounded-xl p-3 shadow-sm border border-slate-200 mb-4">
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Search order, email, suburb..."
                  value={filters.search}
                  onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                  className="pl-10 h-9 text-sm" data-testid="search-input"
                />
              </div>
            </div>
            <Select value={filters.status} onValueChange={(v) => setFilters({ ...filters, status: v })}>
              <SelectTrigger className="w-[160px] h-9 text-xs" data-testid="status-filter"><SelectValue placeholder="Status" /></SelectTrigger>
              <SelectContent>
                {[
                  ["all", "All Statuses"],
                  ["pending", "Pending"],
                  ["confirmed", "Confirmed"],
                  ["ready_for_delivery", "Ready for Delivery"],
                  ["delivered", "Delivered"],
                  ["cancelled", "Cancelled"]
                ].map(([value, label]) => (
                  <SelectItem key={value} value={value}>{label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filters.dateRange} onValueChange={handleDateRangeChange}>
              <SelectTrigger className="w-[130px] h-9 text-xs" data-testid="date-filter">
                <Calendar className="w-3 h-3 mr-1" /><SelectValue placeholder="Date Range" />
              </SelectTrigger>
              <SelectContent>
                {[["all", "All Time"], ["today", "Today"], ["7days", "7 Days"], ["30days", "30 Days"], ["90days", "90 Days"]].map(([v, l]) => (
                  <SelectItem key={v} value={v}>{l}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="flex items-center gap-1">
              <Input type="date" value={filters.startDate} onChange={(e) => setFilters({ ...filters, dateRange: "custom", startDate: e.target.value })} className="min-w-[140px] w-auto h-9 text-xs px-2" data-testid="start-date" />
              <span className="text-slate-400 text-xs">to</span>
              <Input type="date" value={filters.endDate} onChange={(e) => setFilters({ ...filters, dateRange: "custom", endDate: e.target.value })} className="min-w-[140px] w-auto h-9 text-xs px-2" data-testid="end-date" />
            </div>
            <span className="text-xs text-slate-500">{isDriver ? orders.length : pagination.total_count} orders</span>
          </div>
        </div>

        {/* Orders Table */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="text-left px-3 py-2.5 font-semibold text-slate-600 uppercase text-[10px]">Order</th>
                  {!isDriver && <th className="text-left px-3 py-2.5 font-semibold text-slate-600 uppercase text-[10px]">Customer</th>}
                  {!isDriver && <th className="text-left px-3 py-2.5 font-semibold text-slate-600 uppercase text-[10px]">Tier</th>}
                  <th className="text-left px-3 py-2.5 font-semibold text-slate-600 uppercase text-[10px]">Items / Stores</th>
                  <th className="text-left px-3 py-2.5 font-semibold text-slate-600 uppercase text-[10px]">Gross Total</th>
                  {!isDriver && <th className="text-left px-3 py-2.5 font-semibold text-slate-600 uppercase text-[10px]">Delivery</th>}
                  {!isDriver && <th className="text-left px-3 py-2.5 font-semibold text-slate-600 uppercase text-[10px]">Conv. Fee</th>}
                  {!isDriver && <th className="text-left px-3 py-2.5 font-semibold text-slate-600 uppercase text-[10px]">Payment</th>}
                  <th className="text-left px-3 py-2.5 font-semibold text-slate-600 uppercase text-[10px]">Status</th>
                  <th className="text-left px-3 py-2.5 font-semibold text-slate-600 uppercase text-[10px]">Delivery Driver</th>
                  <th className="text-left px-3 py-2.5 font-semibold text-slate-600 uppercase text-[10px]">Date & Time</th>
                  {isDriver && <th className="text-left px-3 py-2.5 font-semibold text-slate-600 uppercase text-[10px]">Delivery Address</th>}
                  <th className="text-left px-3 py-2.5 font-semibold text-slate-600 uppercase text-[10px]">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {loading ? (
                  <tr><td colSpan={isDriver ? 8 : 13} className="px-4 py-12 text-center"><RefreshCw className="w-6 h-6 animate-spin text-sky-600 mx-auto" /></td></tr>
                ) : orders.length === 0 ? (
                  <tr><td colSpan={isDriver ? 8 : 13} className="px-4 py-12 text-center text-slate-500">
                    <ShoppingBag className="w-10 h-10 mx-auto mb-2 text-slate-300" />
                    <p>{isDriver ? "No deliveries assigned to you" : "No orders found"}</p>
                  </td></tr>
                ) : (
                  orders.map((order) => {
                    const storeTags = [...new Set((order.items || []).map(i => i.store_tag).filter(Boolean))];
                    const deliveryDate = order.delivery_details?.delivery_date || order.delivery_date || "";
                    const deliveryTime = order.delivery_details?.delivery_time || order.delivery_time || "";
                    const deliveryAddress = order.delivery_address || 
                      `${order.delivery_details?.address || ""}, ${order.delivery_details?.suburb || ""} ${order.delivery_details?.postcode || ""}`;
                    
                    return (
                      <tr key={order.id} className="hover:bg-slate-50/50" data-testid={`order-row-${order.id}`}>
                        <td className="px-3 py-2.5">
                          <span className="font-mono text-xs font-semibold text-slate-900">{order.order_number || `BC-${order.id?.slice(0, 6).toUpperCase()}`}</span>
                          <br />
                          <span className="text-[10px] text-slate-400">{order.created_at ? format(parseISO(order.created_at), "HH:mm") : ""}</span>
                        </td>
                        {!isDriver && (
                          <td className="px-3 py-2.5">
                            <p className="font-medium text-slate-900 text-xs">{order.customer_name || "N/A"}</p>
                            <div className="flex items-center gap-1.5 mt-0.5">
                              {order.customer_phone && (
                                <a href={`tel:${order.customer_phone}`} className="text-sky-600 hover:text-sky-800 flex items-center gap-0.5" title="Call">
                                  <Phone className="w-2.5 h-2.5" /><span className="text-[10px]">{order.customer_phone}</span>
                                </a>
                              )}
                            </div>
                            <p className="text-[10px] text-slate-400 truncate max-w-[140px]">{order.email}</p>
                            <p className="text-[10px] text-slate-400">{order.delivery_details?.suburb}</p>
                          </td>
                        )}
                        {!isDriver && (
                          <td className="px-3 py-2.5">
                            <div className="flex flex-col gap-1">
                              <MemberBadge isMember={order.is_member_user} />
                              {order.is_vip && <VipBadge count={order.vip_order_count} />}
                            </div>
                          </td>
                        )}
                        <td className="px-3 py-2.5">
                          <span className="text-xs font-medium">{order.items?.length || order.item_count || 0} items</span>
                          <div className="flex gap-0.5 mt-1 flex-wrap">
                            {storeTags.map(tag => <StoreTag key={tag} tag={tag} />)}
                          </div>
                        </td>
                        <td className="px-3 py-2.5 font-semibold text-xs">${order.total?.toFixed(2)}</td>
                        {!isDriver && <td className="px-3 py-2.5 text-xs text-slate-600">${order.delivery_fee?.toFixed(2)}</td>}
                        {!isDriver && (
                          <td className="px-3 py-2.5 text-xs text-slate-600">
                            ${order.convenience_fee?.toFixed(2)}
                            <span className="text-[9px] text-slate-400 block">{order.is_member_order ? "4%" : "7%"}</span>
                          </td>
                        )}
                        {!isDriver && <td className="px-3 py-2.5"><PaymentBadge status={order.payment_status} transactionId={order.transaction_id} /></td>}
                        <td className="px-3 py-2.5"><StatusBadge status={order.order_status} /></td>
                        <td className="px-3 py-2.5">
                          {isDriver ? (
                            <span className="text-xs text-slate-600">{order.delivery_driver || "Assigned"}</span>
                          ) : (
                            <Input 
                              value={order.delivery_driver || ""} 
                              placeholder="Assign driver"
                              className="h-7 text-xs w-[100px]"
                              onChange={(e) => {
                                const newOrders = orders.map(o => 
                                  o.id === order.id ? {...o, delivery_driver: e.target.value} : o
                                );
                                setOrders(newOrders);
                              }}
                              onBlur={(e) => {
                                if (e.target.value) {
                                  updateReconciliation(order.id, { delivery_driver: e.target.value });
                                }
                              }}
                            />
                          )}
                        </td>
                        <td className="px-3 py-2.5 text-xs text-slate-500">
                          <div>
                            {order.created_at ? format(parseISO(order.created_at), "dd MMM yyyy") : "-"}
                          </div>
                          {deliveryDate && (
                            <div className="text-[10px] text-sky-600 mt-0.5">
                              <Calendar className="w-2.5 h-2.5 inline mr-0.5" />
                              {formatDeliveryDate(deliveryDate)}{deliveryTime ? `, ${deliveryTime}` : ""}
                            </div>
                          )}
                        </td>
                        {isDriver && (
                          <td className="px-3 py-2.5 text-xs text-slate-600">
                            <div className="flex items-start gap-1">
                              <MapPin className="w-3 h-3 text-slate-400 mt-0.5 flex-shrink-0" />
                              <span className="text-[10px] max-w-[150px]">{deliveryAddress}</span>
                            </div>
                          </td>
                        )}
                        <td className="px-3 py-2.5">
                          <div className="flex items-center gap-1">
                            <TooltipProvider>
                              <TooltipUI>
                                <TooltipTrigger asChild>
                                  <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => openOrderDetail(order)} data-testid={`view-order-${order.id}`}>
                                    <Eye className="w-3.5 h-3.5" />
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p>View Order</p>
                                </TooltipContent>
                              </TooltipUI>
                            </TooltipProvider>
                            <Select value={order.order_status} onValueChange={(v) => updateOrderStatus(order.order_number || order.id, v)}>
                              <SelectTrigger className="w-[90px] h-7 text-[10px]"><SelectValue /></SelectTrigger>
                              <SelectContent>
                                {isDriver ? (
                                  // Driver can only see limited statuses
                                  driverAllowedStatuses.map(s => (
                                    <SelectItem key={s} value={s}>{s.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase())}</SelectItem>
                                  ))
                                ) : (
                                  // Founders see all statuses
                                  ["pending", "confirmed", "ready_for_delivery", "delivered", "cancelled"].map(s => (
                                    <SelectItem key={s} value={s}>{s.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase())}</SelectItem>
                                  ))
                                )}
                              </SelectContent>
                            </Select>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {pagination.total_pages > 1 && (
            <div className="flex items-center justify-between px-4 py-2 border-t border-slate-200 bg-slate-50">
              <p className="text-xs text-slate-600">Page {pagination.page}/{pagination.total_pages} ({pagination.total_count} orders)</p>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => fetchData(pagination.page - 1)} disabled={!pagination.has_prev || loading}>
                  <ChevronLeft className="w-3 h-3 mr-1" />Prev
                </Button>
                <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => fetchData(pagination.page + 1)} disabled={!pagination.has_next || loading}>
                  Next<ChevronRight className="w-3 h-3 ml-1" />
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Order Detail Modal */}
        {selectedOrder && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
              {/* Modal Header */}
              <div className="p-5 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white rounded-t-2xl z-10">
                <div className="flex items-center gap-3">
                  <h2 className="text-lg font-bold text-slate-900">
                    {selectedOrder.order_number || `BC-${selectedOrder.id?.slice(0, 6).toUpperCase()}`}
                  </h2>
                  <MemberBadge isMember={selectedOrder.is_member_user} />
                  {selectedOrder.is_vip && <VipBadge count={selectedOrder.vip_order_count} />}
                </div>
                <Button variant="ghost" size="sm" onClick={() => setSelectedOrder(null)}>
                  <XCircle className="w-5 h-5" />
                </Button>
              </div>

              <div className="p-5 space-y-5">
                {/* Status Row */}
                <div className="flex flex-wrap gap-4">
                  <div><p className="text-[10px] text-slate-500 mb-0.5">Order Status</p><StatusBadge status={selectedOrder.order_status} /></div>
                  <div><p className="text-[10px] text-slate-500 mb-0.5">Payment</p><PaymentBadge status={selectedOrder.payment_status} transactionId={selectedOrder.transaction_id} /></div>
                  <div><p className="text-[10px] text-slate-500 mb-0.5">Date</p><span className="text-xs">{selectedOrder.created_at ? format(parseISO(selectedOrder.created_at), "dd MMM yyyy, HH:mm") : "-"}</span></div>
                </div>

                {/* Customer Info */}
                <div>
                  <h3 className="text-xs font-semibold text-slate-900 mb-2 flex items-center gap-1"><User className="w-3.5 h-3.5" /> Customer</h3>
                  <div className="bg-slate-50 rounded-lg p-3 text-xs space-y-1">
                    <div className="flex items-center gap-4">
                      <span><strong>Name:</strong> {selectedOrder.customer_name || "N/A"}</span>
                      {selectedOrder.customer_phone && (
                        <a href={`tel:${selectedOrder.customer_phone}`} className="text-sky-600 hover:underline flex items-center gap-1">
                          <Phone className="w-3 h-3" />{selectedOrder.customer_phone}
                        </a>
                      )}
                    </div>
                    <p className="flex items-center gap-1"><Mail className="w-3 h-3 text-slate-400" />{selectedOrder.email}</p>
                    <p className="flex items-center gap-1"><MapPin className="w-3 h-3 text-slate-400" />{selectedOrder.delivery_details?.address}, {selectedOrder.delivery_details?.suburb} {selectedOrder.delivery_details?.postcode} {selectedOrder.delivery_details?.state}</p>
                    <p><strong>Delivery:</strong> {formatDeliveryDate(selectedOrder.delivery_details?.delivery_date)} at {selectedOrder.delivery_details?.delivery_time}</p>
                    {selectedOrder.delivery_details?.special_instructions && (
                      <p className="bg-yellow-50 p-2 rounded border border-yellow-100"><strong>Instructions:</strong> {selectedOrder.delivery_details.special_instructions}</p>
                    )}
                  </div>
                </div>

                {/* Items by Store */}
                <div>
                  <h3 className="text-xs font-semibold text-slate-900 mb-2 flex items-center gap-1"><Package className="w-3.5 h-3.5" /> Items ({selectedOrder.items?.length})</h3>
                  <div className="space-y-1.5 max-h-52 overflow-y-auto">
                    {selectedOrder.items?.map((item, idx) => (
                      <div key={idx} className="flex justify-between items-center bg-slate-50 rounded-lg px-3 py-2">
                        <div className="flex items-center gap-2">
                          <StoreTag tag={item.store_tag} />
                          <div>
                            <p className="text-xs font-medium">
                              {item.url ? (
                                <a href={item.url} target="_blank" rel="noopener noreferrer" className="text-sky-600 hover:text-sky-800 hover:underline" data-testid={`item-source-link-${idx}`}>
                                  {item.product_name}
                                </a>
                              ) : (
                                item.product_name
                              )}
                            </p>
                            <p className="text-[10px] text-slate-500">{item.store_name || item.store || item.retailer}</p>
                            <p className="text-[10px] text-slate-400">Qty: {item.quantity} x ${item.price?.toFixed(2)}</p>
                          </div>
                        </div>
                        <p className="text-xs font-semibold">${(item.price * item.quantity).toFixed(2)}</p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Financial Breakdown */}
                <div className="border-t border-slate-200 pt-4">
                  <h3 className="text-xs font-semibold text-slate-900 mb-2 flex items-center gap-1"><DollarSign className="w-3.5 h-3.5" /> Financial Breakdown</h3>
                  <div className="space-y-1.5 text-xs">
                    <div className="flex justify-between"><span className="text-slate-500">Subtotal</span><span>${selectedOrder.subtotal?.toFixed(2)}</span></div>
                    <div className="flex justify-between"><span className="text-slate-500">Delivery Fee</span><span>${selectedOrder.delivery_fee?.toFixed(2)}</span></div>
                    <div className="flex justify-between"><span className="text-slate-500">Convenience Fee ({selectedOrder.is_member_order ? "4%" : "7%"})</span><span>${selectedOrder.convenience_fee?.toFixed(2)}</span></div>
                    {(selectedOrder.membership_fee || 0) > 0 && (
                      <div className="flex justify-between"><span className="text-slate-500">Membership Fee</span><span>${selectedOrder.membership_fee?.toFixed(2)}</span></div>
                    )}
                    <Separator />
                    <div className="flex justify-between text-base font-bold"><span>Gross Total</span><span>${selectedOrder.total?.toFixed(2)}</span></div>
                  </div>
                </div>

                {/* Reconciliation Section */}
                <div className="border-t border-slate-200 pt-4">
                  <h3 className="text-xs font-semibold text-slate-900 mb-3 flex items-center gap-1"><Save className="w-3.5 h-3.5" /> Receipt Reconciliation</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <label className="text-[10px] font-medium text-slate-600 mb-0.5 block">Coles Receipt ID</label>
                      <Input
                        value={reconciliation.coles_receipt_id}
                        onChange={(e) => setReconciliation({ ...reconciliation, coles_receipt_id: e.target.value.slice(0, 200) })}
                        placeholder="Enter Coles receipt ID"
                        className="h-8 text-xs"
                        maxLength={200}
                        data-testid="reconciliation-coles"
                      />
                    </div>
                    <div>
                      <label className="text-[10px] font-medium text-slate-600 mb-0.5 block">Woolworths Receipt ID</label>
                      <Input
                        value={reconciliation.woolworths_receipt_id}
                        onChange={(e) => setReconciliation({ ...reconciliation, woolworths_receipt_id: e.target.value.slice(0, 200) })}
                        placeholder="Enter Woolworths receipt ID"
                        className="h-8 text-xs"
                        maxLength={200}
                        data-testid="reconciliation-woolworths"
                      />
                    </div>
                    <div>
                      <label className="text-[10px] font-medium text-slate-600 mb-0.5 block">ChemistWH Receipt ID</label>
                      <Input
                        value={reconciliation.chemist_receipt_id}
                        onChange={(e) => setReconciliation({ ...reconciliation, chemist_receipt_id: e.target.value.slice(0, 200) })}
                        placeholder="Enter Chemist Warehouse receipt ID"
                        className="h-8 text-xs"
                        maxLength={200}
                        data-testid="reconciliation-chemist"
                      />
                    </div>
                    <div>
                      <label className="text-[10px] font-medium text-slate-600 mb-0.5 block">Delivery Driver</label>
                      <Input
                        value={reconciliation.delivery_driver}
                        onChange={(e) => setReconciliation({ ...reconciliation, delivery_driver: e.target.value.slice(0, 200) })}
                        placeholder="Assigned driver name"
                        className="h-8 text-xs"
                        maxLength={200}
                        data-testid="reconciliation-driver"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="text-[10px] font-medium text-slate-600 mb-0.5 block">Admin Comments</label>
                      <Textarea
                        value={reconciliation.admin_comments}
                        onChange={(e) => setReconciliation({ ...reconciliation, admin_comments: e.target.value.slice(0, 200) })}
                        placeholder="Internal notes about this order..."
                        className="text-xs"
                        rows={2}
                        maxLength={200}
                        data-testid="reconciliation-comments"
                      />
                      <p className="text-[9px] text-slate-400 mt-0.5 text-right">{reconciliation.admin_comments?.length || 0}/200</p>
                    </div>
                  </div>
                  <Button
                    onClick={saveReconciliation}
                    disabled={savingReconciliation}
                    size="sm"
                    className="mt-3"
                    data-testid="save-reconciliation-btn"
                  >
                    <Save className="w-3.5 h-3.5 mr-1" />
                    {savingReconciliation ? "Saving..." : "Save Reconciliation"}
                  </Button>
                </div>

                {/* Quick Actions */}
                <div className="flex gap-2 border-t border-slate-200 pt-4">
                  {selectedOrder.order_status === "pending" && (
                    <Button size="sm" onClick={() => { updateOrderStatus(selectedOrder.id, "confirmed"); setSelectedOrder(null); }}>
                      <CheckCircle className="w-3.5 h-3.5 mr-1" />Confirm
                    </Button>
                  )}
                  {selectedOrder.order_status === "confirmed" && (
                    <Button size="sm" onClick={() => { updateOrderStatus(selectedOrder.id, "ready_for_delivery"); setSelectedOrder(null); }}>
                      <Package className="w-3.5 h-3.5 mr-1" />Ready for Delivery
                    </Button>
                  )}
                  <Button variant="outline" size="sm" onClick={() => setSelectedOrder(null)}>Close</Button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FoundersDashboard;
